/**
 * 商品设计合成成品图写入图片空间（经 ensureImageSpaceItemsServerUrl 落 OSS），
 * 归属个人/团队与 IPE 设计器上传一致。
 */
import { imageSpaceAPI } from '@/utils/api';
import { isPersistedImageUrl } from '@/utils/imageUrlUtils';
import { estimateDataUrlByteSize } from '@/utils/imageSpaceByteSize';
import { runWithImageSpaceIngestContextAsync } from '@/utils/imageSpaceIngestContext';
import { getActiveImageSpaceCreatorPayload } from '@/utils/imageSpaceCreatorMeta';

export function resolveGoodsCompositeBillingNav(billingNav) {
  return billingNav === 'podProductDesign' ? 'podProductDesign' : 'goods';
}

export function buildGoodsCompositeImageSpaceId({
  taskId,
  templateId,
  groupIndex,
  imageId,
}) {
  return `COMP-${taskId}-${templateId}-${groupIndex}-${imageId}`;
}

function pickCompositeDataUrl(item) {
  const raw = item?.dataUrl || item?.compositeUrl || item?.url || '';
  return String(raw || '').trim();
}

function buildCompositeDisplayName({
  templateName,
  groupIndex,
  imageId,
  slideName,
  fallbackName,
}) {
  if (fallbackName) return String(fallbackName).trim();
  const groupLabel = Number.isFinite(Number(groupIndex)) ? `第 ${Number(groupIndex) + 1} 组` : '';
  const slideLabel = slideName || imageId || '';
  const parts = [templateName, groupLabel, slideLabel].filter(Boolean);
  return parts.length ? parts.join(' · ') : '商品成品图';
}

function toImageSpaceRecord({
  taskId,
  item,
  templateName,
  slideName,
}) {
  const dataUrl = pickCompositeDataUrl(item);
  if (!dataUrl) return null;

  const templateId = item.templateId;
  const groupIndex = item.groupIndex;
  const imageId = item.imageId;
  if (!taskId || !templateId || groupIndex == null || !imageId) return null;

  const id = buildGoodsCompositeImageSpaceId({
    taskId,
    templateId,
    groupIndex,
    imageId,
  });

  const isInline = dataUrl.startsWith('data:image/');
  const persisted = !isInline && isPersistedImageUrl(dataUrl);

  return {
    id,
    name: buildCompositeDisplayName({
      templateName,
      groupIndex,
      imageId,
      slideName,
      fallbackName: item.name,
    }),
    url: persisted ? dataUrl : (isInline ? '' : dataUrl),
    dataUrl: isInline ? dataUrl : null,
    width: item.width ?? null,
    height: item.height ?? null,
    size: estimateDataUrlByteSize(dataUrl) || item.size || null,
    taskId: String(taskId),
    templateId: String(templateId),
    groupIndex: Number(groupIndex),
    imageId: String(imageId),
  };
}

/**
 * @param {object} options
 * @param {string} options.taskId
 * @param {Array<object>} options.items composite 行（含 dataUrl/compositeUrl、templateId、groupIndex、imageId）
 * @param {string} [options.billingNav] goods | podProductDesign
 * @param {object|null} [options.creatorPayload] 个人/团队归属；缺省用当前登录上下文
 * @param {string} [options.templateName]
 * @param {Record<string,string>} [options.templateNameById]
 * @param {Record<string,string>} [options.slideNameById]
 */
export async function saveGoodsCompositeResultsToImageSpace({
  taskId,
  items = [],
  billingNav = 'goods',
  creatorPayload = null,
  templateName = '',
  templateNameById = {},
  slideNameById = {},
} = {}) {
  const id = String(taskId || '').trim();
  if (!id || !Array.isArray(items) || items.length === 0) return [];

  const records = items
    .map((item) => toImageSpaceRecord({
      taskId: id,
      item,
      templateName: templateNameById?.[item?.templateId] || templateName,
      slideName: slideNameById?.[item?.imageId] || '',
    }))
    .filter(Boolean);

  if (records.length === 0) return [];

  const navId = resolveGoodsCompositeBillingNav(billingNav);
  const payload = creatorPayload || getActiveImageSpaceCreatorPayload();

  try {
    await runWithImageSpaceIngestContextAsync(
      { navId, workflowStepNavId: navId, ingestKind: 'result' },
      async () => {
        await imageSpaceAPI.batchSave(records, {
          ingestKind: 'result',
          creatorPayload: payload,
        });
      },
    );
    return records;
  } catch (error) {
    console.warn('[商品成品图] 写入图片空间失败（不影响合成保存）', error);
    return [];
  }
}
